import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// Store temporarily disabled to prevent loops

// Error Boundaries
import ErrorBoundary from './components/ErrorBoundary';
import ProductErrorBoundary from './components/ProductErrorBoundary';

// Page Analytics
import { PageTracker } from './hooks/usePageTracking';

// UI Components
import { PageLoader } from './components/ui/EnhancedLoader';

// Authentication Components
import AuthPage from './components/auth/AuthPage';
import AdminLoginPage from './components/auth/AdminLoginPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';

// Sitewide Messages
import SitewideMessage from './components/common/SitewideMessage';

// Layout Components
// Main Applications
import MyAccountApp from './components/myAccount/MyAccountApp';
import CustomPlanSubscriptionPage from './components/myAccount/pages/CustomPlanSubscriptionPage';
import EquityManagerApp from './products/equityManager/EquityManagerApp';
import DataroomApp from './products/dataroom/DataroomApp';
import QuestionsApp from './products/questions/QuestionsApp';
import CapRaiseApp from './products/capRaise/CapRaiseApp';
import UserInvitesApp from './products/userInvites/UserInvitesApp';
import MarketDataApp from './products/analysis/MarketDataApp';
import AdminApp from './admin/AdminApp';
import AcceptInvitePage from './components/auth/AcceptInvitePage';

// Development/Testing Components
import CacheDemo from './components/testing/CacheDemo';
import StandaloneCacheTest from './components/testing/StandaloneCacheTest';
import SystemMonitoringTest from './components/testing/SystemMonitoringTest';

// Redirect components that preserve the path
const DealroomsRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace('/dealrooms', '/dealroom');
  return <Navigate to={newPath} replace />;
};

const DataroomRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace('/dataroom', '/dealroom');
  return <Navigate to={newPath} replace />;
};

// Development helpers
if (process.env.NODE_ENV === 'development') {
  // Add global cache control functions
  window.devCache = {
    clear: () => {
      localStorage.clear();
      sessionStorage.clear();
      console.log('🧹 All browser caches cleared');
    },
    status: () => {
      console.log('📊 Cache Status:', {
        localStorage: Object.keys(localStorage).length,
        sessionStorage: Object.keys(sessionStorage).length
      });
    }
  };
}

const App = () => {
    // Simple state without problematic store
    const [loading, setLoading] = useState(true); // Start as loading
    const [user, setUser] = useState(null); // Simple user state
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [maintenanceMessage, setMaintenanceMessage] = useState('');
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    useEffect(() => {
        // Simple auth check without complex store
        const checkAuth = async () => {
            try {
                // Import supabase directly to check auth
                const { supabase } = await import('./config');
                const { data: { session } } = await supabase.auth.getSession();
                
                if (session?.user) {
                    setUser(session.user);
                    
                    // Check if user is super admin
                    const { data: profile } = await supabase
                        .from('user_profiles')
                        .select('is_super_admin')
                        .eq('id', session.user.id)
                        .single();
                    
                    if (profile?.is_super_admin) {
                        setIsSuperAdmin(true);
                    }
                }
            } catch (error) {
                console.log('Auth check failed:', error);
                // Continue without auth
            } finally {
                setLoading(false);
            }
        };

        checkAuth();

        // Listen for auth state changes to update user state
        // Use a ref-like pattern with a promise
        const authListenerPromise = (async () => {
            const { supabase } = await import('./config');
            return supabase.auth.onAuthStateChange((event, session) => {
                if (event === 'SIGNED_IN' && session?.user) {
                    setUser(session.user);
                } else if (event === 'SIGNED_OUT') {
                    setUser(null);
                }
            });
        })();

        return () => {
            authListenerPromise.then(({ data: { subscription } }) => {
                if (subscription && typeof subscription.unsubscribe === 'function') {
                    subscription.unsubscribe();
                }
            }).catch(() => {
                // Ignore errors during cleanup
            });
        };
    }, []);

    // Check maintenance mode
    useEffect(() => {
        const checkMaintenanceMode = async () => {
            try {
                const { supabase } = await import('./config');
                
                // Query platform_settings - will fail gracefully if RLS blocks unauthenticated access
                const { data, error } = await supabase
                    .from('platform_settings')
                    .select('settings')
                    .maybeSingle(); // Use maybeSingle() instead of single() to handle no rows gracefully

                // Handle 406 (Not Acceptable) and other RLS/auth errors gracefully
                if (error) {
                    // 406 typically means RLS policy blocked the request
                    // This is fine - we'll just use default values
                    if (error.code === 'PGRST116' || error.code === '42501' || error.status === 406) {
                        // No data or permission denied - use defaults
                        setMaintenanceMode(false);
                        return;
                    }
                    // For other errors, log but don't fail
                    console.warn('Error checking maintenance mode:', error.message);
                    return;
                }

                if (data?.settings) {
                    setMaintenanceMode(data.settings.maintenance_mode || false);
                    setMaintenanceMessage(data.settings.maintenance_message || 'System under maintenance. We\'ll be back soon!');
                } else {
                    // No settings found - use defaults
                    setMaintenanceMode(false);
                }
            } catch (error) {
                // Catch any unexpected errors and use defaults
                console.warn('Error checking maintenance mode:', error);
                setMaintenanceMode(false);
            }
        };

        checkMaintenanceMode();
        
        // Check every 30 seconds
        const interval = setInterval(checkMaintenanceMode, 30000);
        return () => clearInterval(interval);
    }, []);

    console.log('App render - Simple auth state:', { 
        user: user ? 'present' : 'null',
        loading,
        currentPath: window.location.pathname
    });

    // Show loading screen while checking auth
    if (loading) {
        return <PageLoader text="Initializing application..." />;
    }

    // Check maintenance mode - block non-super admins
    if (maintenanceMode && !isSuperAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                    <div className="mb-6">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                            <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">System Maintenance</h1>
                    <p className="text-gray-600 mb-6">{maintenanceMessage}</p>
                    <p className="text-sm text-gray-500">We apologize for any inconvenience. Please check back soon.</p>
                </div>
            </div>
        );
    }

    // Check for development routes first (before auth check)
    if (process.env.NODE_ENV === 'development') {
        const currentPath = window.location.pathname;
        if (currentPath === '/cache-test') {
            return (
                <ErrorBoundary showDetails={true}>
                    <StandaloneCacheTest />
                </ErrorBoundary>
            );
        }
        if (currentPath === '/test-cache') {
            return (
                <ErrorBoundary showDetails={true}>
                    <CacheDemo />
                </ErrorBoundary>
            );
        }
        if (currentPath === '/monitoring-test') {
            return (
                <ErrorBoundary showDetails={true}>
                    <SystemMonitoringTest />
                </ErrorBoundary>
            );
        }
    }

    // Simple fallback for debugging
    if (!user) {
        console.log('No user, showing login page');
        return (
            <ErrorBoundary showDetails={process.env.NODE_ENV === 'development'}>
                <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    {/* Zone 1 - Top bar across all pages */}
                    <SitewideMessage zone="zone1" userRoles={[]} />
                    <Routes>
                        <Route path="/" element={<AuthPage />} />
                        <Route path="/login" element={<AuthPage />} />
                        <Route path="/admin-login" element={<AdminLoginPage />} />
                        <Route path="/accept-invite" element={<AcceptInvitePage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        {/* Custom Plan Subscription (Public - no auth required) */}
                        <Route path="/subscribe/:token" element={
                            <ProductErrorBoundary productName="Subscription">
                                <CustomPlanSubscriptionPage />
                            </ProductErrorBoundary>
                        } />
                        <Route path="*" element={<Navigate to="/login" />} />
                    </Routes>
                </Router>
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary showDetails={process.env.NODE_ENV === 'development'}>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                {/* Zone 1 - Top bar across all pages */}
                <SitewideMessage zone="zone1" userRoles={[]} />
                <PageTracker />
                <Routes>
                {/* Public / Auth Routes */}
                <Route path="/login" element={<AuthPage />} />
                <Route path="/admin-login" element={<AdminLoginPage />} />
                <Route path="/accept-invite" element={<AcceptInvitePage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                
                    {/* Custom Plan Subscription (Public) */}
                    <Route path="/subscribe/:token" element={
                        <ProductErrorBoundary productName="Subscription">
                            <CustomPlanSubscriptionPage />
                        </ProductErrorBoundary>
                    } />
                    
                
                {/* Protected Routes - Direct routing */}
                <Route path="/" element={user ? <Navigate to="/my-account" /> : <Navigate to="/login" />} />
                
                <Route path="/my-account/*" element={
                    <ProductErrorBoundary productName="My Account">
                        <MyAccountApp user={user} />
                    </ProductErrorBoundary>
                } />
                <Route path="/account" element={<Navigate to="/my-account" />} />
                <Route path="/equity/*" element={
                    <ProductErrorBoundary productName="Equity Manager">
                        <EquityManagerApp user={user} />
                    </ProductErrorBoundary>
                } />
                {/* Redirect /dealrooms (plural) to /dealroom (singular) */}
                <Route path="/dealrooms" element={<Navigate to="/dealroom" replace />} />
                <Route path="/dealrooms/*" element={<DealroomsRedirect />} />
                
                {/* Redirect /dataroom (old URL) to /dealroom (new URL) for backward compatibility */}
                <Route path="/dataroom" element={<Navigate to="/dealroom" replace />} />
                <Route path="/dataroom/*" element={<DataroomRedirect />} />
                
                <Route path="/dealroom/*" element={
                    <ProductErrorBoundary productName="Dealroom">
                        <DataroomApp user={user} />
                    </ProductErrorBoundary>
                } />
                <Route path="/questions/*" element={
                    <ProductErrorBoundary productName="Questions">
                        <QuestionsApp user={user} />
                    </ProductErrorBoundary>
                } />
                <Route path="/capraise/*" element={
                    <ProductErrorBoundary productName="CapRaise">
                        <CapRaiseApp user={user} />
                    </ProductErrorBoundary>
                } />
                <Route path="/userinvites/*" element={
                    <ProductErrorBoundary productName="User Invites">
                        <UserInvitesApp user={user} />
                    </ProductErrorBoundary>
                } />
                <Route path="/market-data/*" element={
                    <ProductErrorBoundary productName="MarketData">
                        <MarketDataApp user={user} />
                    </ProductErrorBoundary>
                } />
                <Route path="/analysis/*" element={<LegacyMarketDataRedirect />} />

                {/* Admin Route */}
                <Route path="/adminhq/*" element={<AdminApp user={user} />} />

                {/* Catch-all for non-matching routes */}
                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            </Router>
        </ErrorBoundary>
    );
};

const LegacyMarketDataRedirect = () => {
    const location = useLocation();
    const newPath = location.pathname.replace('/analysis', '/market-data');
    return <Navigate to={newPath} replace />;
};

export default App;
